import type { ReactNode } from "react";
import {
  Modal as RNModal,
  Pressable,
  StyleSheet,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import type { UiTheme } from "../theme";

// THE shared scrim overlay — the one place the modal backdrop (scrim colour,
// tap-out dismiss, press-swallow) is encoded. Collapses the hand-rolled
// `<Modal transparent> → Pressable backdrop → Pressable swallow` chrome that
// ~13 sheets/dialogs each re-rolled with a DIFFERENT black-alpha literal
// (#0008 / rgba(0,0,0,0.4|0.45|0.5)) into one primitive reading
// `palette.scrim`. Composed by <Sheet> (bottom) and <Dialog> (centred); rarely
// mounted directly. Pure-RN, so it renders identically on mobile and web-rnw.
//
// ⚠️ Never mount a native Fabric component (e.g. Slider) inside the children —
// the New Arch crashes an RN Modal that hosts one. The RestStepper is a pure
// <Pressable> stepper for exactly this reason (see components/RestStepper.tsx).
// Keep modal contents pure-RN.

export type ModalProps = {
  theme: UiTheme;
  visible: boolean;
  onRequestClose: () => void;
  /**
   * Backdrop tap dismisses. Default true (sheets + tap-out dialogs); pass
   * false for flows that force an explicit Cancel button (e.g. the stepper
   * dialogs, where a stray tap must not discard an edit).
   */
  dismissOnBackdropPress?: boolean;
  animationType?: "slide" | "fade" | "none";
  /**
   * Backdrop layout override — the composer sets the card placement here:
   * <Sheet> pins to the bottom (`justifyContent:'flex-end'`), <Dialog>
   * centres (`justifyContent/alignItems:'center'` + padding).
   */
  backdropStyle?: StyleProp<ViewStyle>;
  children: ReactNode;
};

export function Modal({
  theme,
  visible,
  onRequestClose,
  dismissOnBackdropPress = true,
  animationType = "slide",
  backdropStyle,
  children,
}: ModalProps) {
  const styles = makeStyles(theme);
  return (
    <RNModal
      visible={visible}
      transparent
      animationType={animationType}
      onRequestClose={onRequestClose}
    >
      <Pressable
        accessibilityViewIsModal
        style={[styles.backdrop, backdropStyle]}
        onPress={dismissOnBackdropPress ? onRequestClose : undefined}
      >
        {/* Swallow taps on the card so they don't bubble up and dismiss. */}
        <Pressable style={styles.swallow} onPress={() => {}}>
          {children}
        </Pressable>
      </Pressable>
    </RNModal>
  );
}

function makeStyles(theme: UiTheme) {
  return StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: theme.palette.scrim },
    swallow: { width: "100%" },
  });
}
